import React, { useCallback, useState } from "react";
import { AppBar, Toolbar, IconButton, Typography, Box, CircularProgress, Alert, Button } from "@mui/material";
import { Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import usePaginatedList from "../hooks/usePaginatedList";
import OrderCard from "../components/OrderCard";
import LoadMoreButton from "../components/LoadMoreButton";
import { NAV_HEIGHT } from "../components/MainLayout";

export default function Pickup() {
  const { store, user, logout } = useAuth();
  const navigate = useNavigate();
  const currency = store?.currency || store?.price_type || "";

  const [selected, setSelected] = useState([]);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");

  // GET pos/v1/orders/get-orders?st=delivery_pending&page=N —
  // OrderController@getOrders, scoped to order_type=delivery && status=dispatched.
  // Visible to every driver at the store until one of them claims it below.
  const fetchPage = useCallback(
    (page) =>
      apiClient.get("orders/get-orders", { params: { st: "delivery_pending", page } }).then((res) => {
        const paginator = res.data?.orders || {};
        return { items: paginator.data || [], hasMore: (paginator.current_page || 1) < (paginator.last_page || 1) };
      }),
    []
  );
  const { items: orders, loading, loadingMore, hasMore, error, loadMore, reload } = usePaginatedList(fetchPage, []);

  const toggleSelect = (order) => {
    setSelected((prev) => (prev.includes(order.id) ? prev.filter((id) => id !== order.id) : [...prev, order.id]));
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleMarkPickedUp = async () => {
    setClaiming(true);
    setClaimError("");
    // orders/update-ord-status is a single-order endpoint — claim each
    // selected order individually. allSettled so one failure (e.g. another
    // driver just claimed the same order) doesn't stop the rest of the batch.
    const results = await Promise.allSettled(
      selected.map((id) => apiClient.post("orders/update-ord-status", { id, status: "delivery_picked_up", delivery_by: user?.id }))
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      setClaimError(`${failed} of ${selected.length} order${selected.length === 1 ? "" : "s"} could not be marked picked up.`);
    }
    setSelected([]);
    setClaiming(false);
    reload();
  };

  return (
    <Box sx={{ pb: selected.length > 0 ? `${88 + NAV_HEIGHT}px` : 0 }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            Ready for Pickup
          </Typography>
          <IconButton onClick={handleLogout} size="small">
            <Logout fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {(error || claimError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {claimError || error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            No orders waiting for pickup.
          </Typography>
        ) : (
          <>
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} currency={currency} selectable selected={selected.includes(o.id)} onToggleSelect={toggleSelect} />
            ))}
            <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
          </>
        )}
      </Box>

      {selected.length > 0 && (
        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: NAV_HEIGHT, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", p: 2, zIndex: 15 }}>
          <Button fullWidth variant="contained" size="large" disabled={claiming} onClick={handleMarkPickedUp} sx={{ py: 1.4 }}>
            {claiming ? "Marking..." : `Mark Picked Up (${selected.length})`}
          </Button>
        </Box>
      )}
    </Box>
  );
}
